import React, { useState, useEffect } from 'react';
import BackButton from '../../components/common/UI/BackButton';
import SuccessModal from '../../components/common/UI/SuccessModal';
import { useDeposit } from '../../hooks/useDeposit';
import { API_BASE_URL } from '../../utils/constants';
import './Deposit.css';

// ========================================
// SECURITY VALIDATION CLASS
// ========================================
class DepositSecurityValidator {
  // SQL injection patterns
  static SQL_PATTERNS = [
    /\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|SCRIPT)\b/i,
    /--/,        // SQL comment
    /\/\*/,      // SQL comment start
    /\*\//,      // SQL comment end
    /;/,         // SQL statement terminator
    /xp_/i,      // Extended stored procedures
    /sp_/i,      // Stored procedures
  ];

  // XSS patterns
  static XSS_PATTERNS = [
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onclick=/i,
    /onload=/i,
    /<iframe/i,
    /<object/i,
    /<embed/i,
  ];

  /**
   * Check if input contains SQL injection patterns
   */
  static containsSQLInjection(value) {
    if (typeof value !== 'string') return false;
    return this.SQL_PATTERNS.some(pattern => pattern.test(value));
  }

  /**
   * Check if input contains XSS patterns
   */
  static containsXSS(value) {
    if (typeof value !== 'string') return false;
    return this.XSS_PATTERNS.some(pattern => pattern.test(value));
  }

  /**
   * Validate blockchain transaction hash
   * - 60-70 characters
   * - Hexadecimal only (0-9, a-f, A-F)
   * - May have 0x prefix
   */
  static validateTransactionHash(txHash) {
    if (!txHash) {
      throw new Error('Transaction hash is required');
    }

    const trimmed = txHash.trim();

    // Check for SQL injection
    if (this.containsSQLInjection(trimmed)) {
      throw new Error('Invalid transaction hash format. SQL queries not allowed.');
    }

    // Check for XSS
    if (this.containsXSS(trimmed)) {
      throw new Error('Invalid transaction hash format. HTML/Script tags not allowed.');
    }

    // Remove 0x prefix if present
    let cleanHash = trimmed.toLowerCase();
    if (cleanHash.startsWith('0x')) {
      cleanHash = cleanHash.slice(2);
    }

    // Check length
    if (cleanHash.length < 60 || cleanHash.length > 70) {
      throw new Error('Transaction hash must be 60-70 characters long');
    }

    // Check hexadecimal only
    if (!/^[0-9a-f]+$/.test(cleanHash)) {
      throw new Error('Transaction hash must contain only hexadecimal characters (0-9, a-f)');
    }

    return trimmed;
  }

  /**
   * Validate crypto wallet address
   * - 26-50 characters
   * - Alphanumeric only
   */
  static validateWalletAddress(address) {
    if (!address) {
      throw new Error('Wallet address is required');
    }

    const trimmed = address.trim();

    // Check for SQL injection
    if (this.containsSQLInjection(trimmed)) {
      throw new Error('Invalid wallet address format. SQL queries not allowed.');
    }

    // Check for XSS
    if (this.containsXSS(trimmed)) {
      throw new Error('Invalid wallet address format. HTML/Script tags not allowed.');
    }

    // Check length
    if (trimmed.length < 26 || trimmed.length > 50) {
      throw new Error('Wallet address must be 26-50 characters long');
    }

    // Check alphanumeric only
    if (!/^[0-9a-zA-Z]+$/.test(trimmed)) {
      throw new Error('Wallet address must contain only letters and numbers');
    }

    return trimmed;
  }

  /**
   * Validate deposit amount
   */
  static validateAmount(amount, exchangeRate = null) {
    if (!amount) {
      throw new Error('Amount is required');
    }

    const numAmount = parseFloat(amount);

    if (isNaN(numAmount)) {
      throw new Error('Invalid amount format');
    }

    if (exchangeRate && exchangeRate > 0) {
      const minUsd = (2000 / exchangeRate).toFixed(2);
      if (numAmount < parseFloat(minUsd)) {
        throw new Error(`Minimum deposit is ₦2,000 (≈ $${minUsd} at current rate)`);
      }
    }

    if (numAmount > 100000000) {
      throw new Error('Amount exceeds maximum limit');
    }

    return numAmount;
  }
}

const CryptoTransactionForm = ({ network, wallet, onSubmit, onBack }) => {
  const { createCryptoDeposit, isSuccess, successDetails, showSuccess, hideSuccess } = useDeposit();
  const [formData, setFormData] = useState({
    amount: '',
    txHash: '',
    senderWalletAddress: ''
  });
  const [exchangeRate, setExchangeRate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rateLoading, setRateLoading] = useState(true);
  const [rateError, setRateError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch exchange rate
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        setRateLoading(true);
        setRateError(null);
        
        
        console.log('📄 Fetching exchange rate...');
        const url = `${API_BASE_URL}/deposit/exchange-rate/`;
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
        });
        
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await response.text();
          console.error('❌ Non-JSON response:', textResponse.substring(0, 200));
          throw new Error('Server returned non-JSON response');
        }
        
        const data = await response.json();
        console.log('✅ Exchange rate data:', data);
        
        if (data.rate && !isNaN(data.rate)) {
          setExchangeRate(data.rate);
          console.log(`💰 Exchange rate set: $1 = ₦${data.rate}`);
        } else {
          throw new Error('Invalid exchange rate received');
        }
        
      } catch (error) {
        console.error('❌ Error fetching exchange rate:', error);
        setRateError(error.message);
        const fallbackRate = 1500;
        setExchangeRate(fallbackRate);
        console.warn(`⚠️ Using fallback rate: ${fallbackRate}`);
      } finally {
        setRateLoading(false);
      }
    };

    fetchExchangeRate();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    try {
      // Validate amount against ₦2,000 minimum using current rate
      DepositSecurityValidator.validateAmount(formData.amount, exchangeRate);
    } catch (error) {
      errors.amount = error.message;
    }

    try {
      // Validate transaction hash
      DepositSecurityValidator.validateTransactionHash(formData.txHash);
    } catch (error) {
      errors.txHash = error.message;
    }

    try {
      // Validate sender wallet address
      DepositSecurityValidator.validateWalletAddress(formData.senderWalletAddress);
    } catch (error) {
      errors.senderWalletAddress = error.message;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('🚀 Form submitted with data:', formData);

    // Validate form before submission
    if (!validateForm()) {
      console.log('❌ Form validation failed:', validationErrors);
      return;
    }

    setSubmitting(true);

    try {
      console.log('📤 Creating crypto deposit...');
      const response = await createCryptoDeposit({
        admin_wallet_id: wallet.id,
        amount: parseFloat(formData.amount),
        tx_hash: formData.txHash.trim(),
        sender_wallet_address: formData.senderWalletAddress.trim(),
        network: network.id
      });

      console.log('✅ Deposit API response:', response);

      // Show success modal
      showSuccess({
        'Amount': `$${formData.amount}`,
        'Network': network.name,
        'Network Type': network.network_type,
        'Destination Wallet': `${wallet.wallet_address.substring(0, 8)}...${wallet.wallet_address.substring(wallet.wallet_address.length - 8)}`,
        'Transaction Hash': formData.txHash.substring(0, 16) + '...',
        'Sender Wallet': `${formData.senderWalletAddress.substring(0, 8)}...`,
        'Reference ID': response.reference_id || response.id || 'N/A',
        'Status': 'Pending Review'
      });

      // Clear form
      setFormData({
        amount: '',
        txHash: '',
        senderWalletAddress: ''
      });
      setValidationErrors({});

      console.log('✅ Form cleared, success modal should show');

    } catch (error) {
      console.error('❌ Deposit failed:', error);
      alert(`Deposit failed: ${error.message || 'Please try again.'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const calculateNairaAmount = () => {
    if (!formData.amount || !exchangeRate) return '0';
    const nairaAmount = parseFloat(formData.amount) * exchangeRate;
    return nairaAmount.toLocaleString('en-NG', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Derived: minimum USD at current rate, and whether current input is below it
  const minUsdAmount = exchangeRate ? (2000 / exchangeRate).toFixed(2) : null;
  const isBelowMinimum = !formData.amount || !exchangeRate || (parseFloat(formData.amount) * exchangeRate < 2000);

  return (
    <div className="crypto-transaction-form">
      <div className="section-header">
        <BackButton onClick={onBack} />
        <h2>Complete Top-Up</h2>
      </div>

      <form onSubmit={handleSubmit} className="deposit-form">
        <div className="form-section">
          <h3 className="section-title">Destination Wallet Address</h3>
          <div className="wallet-display">
            {wallet.wallet_address}
          </div>
          <p className="instruction">
            Send only {wallet.cryptocurrency} on {network.name} network to this address
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="amount">Amount (USD) <span className="required">*</span></label>
          <input
            type="number"
            id="amount"
            value={formData.amount}
            onChange={(e) => handleInputChange('amount', e.target.value)}
            placeholder="Enter amount in USD"
            step="0.01"
            min="1"
            required
          />
          {validationErrors.amount && (
            <p className="validation-error">⚠️ {validationErrors.amount}</p>
          )}
          
          {rateLoading && (
            <p className="exchange-rate-loading">
              ⏳ Loading exchange rate...
            </p>
          )}
          
          {rateError && (
            <p className="exchange-rate-error">
              ⚠️ {rateError}
              <br />
              <small>Using fallback rate. Your deposit will be processed at the current rate.</small>
            </p>
          )}
          
          {!formData.amount && exchangeRate && !rateLoading && (
            <p className="exchange-rate-note exchange-rate-note--hint">
              {`Minimum amount: ₦2,000 (≈ $${minUsdAmount} at current rate)`}
            </p>
          )}

          {formData.amount && exchangeRate && !rateLoading && (
            <p className={`exchange-rate-note ${isBelowMinimum ? 'exchange-rate-note--below-min' : ''}`}>
              ≈ ₦{calculateNairaAmount()} Naira
              {` (Rate: $1 = ₦${exchangeRate.toLocaleString()})`}
              {isBelowMinimum && (
                <span className="exchange-rate-note__minimum">
                  {` — Minimum is ₦2,000 (≈ $${minUsdAmount})`}
                </span>
              )}
            </p>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="senderWalletAddress">Your Wallet Address <span className="required">*</span></label>
          <input
            type="text"
            id="senderWalletAddress"
            value={formData.senderWalletAddress}
            onChange={(e) => handleInputChange('senderWalletAddress', e.target.value)}
            placeholder="Enter your crypto wallet address"
            required
            maxLength={50}
          />
          {validationErrors.senderWalletAddress && (
            <p className="validation-error">⚠️ {validationErrors.senderWalletAddress}</p>
          )}
          <p className="input-help">
            Enter the wallet address you sent from (26-50 characters, alphanumeric only)
          </p>
        </div>

        <div className="form-group">
          <label htmlFor="txHash">Transaction Hash <span className="required">*</span></label>
          <input
            type="text"
            id="txHash"
            value={formData.txHash}
            onChange={(e) => handleInputChange('txHash', e.target.value)}
            placeholder="Enter transaction hash after sending"
            required
            maxLength={70}
          />
          {validationErrors.txHash && (
            <p className="validation-error">⚠️ {validationErrors.txHash}</p>
          )}
          <p className="input-help">
            Paste your transaction hash here after completing the transfer (60-70 characters, hexadecimal)
          </p>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="change-network-btn"
            onClick={onBack}
          >
            Change Network
          </button>
          <button 
            type="submit" 
            className="submit-deposit-btn"
            disabled={submitting || rateLoading || isBelowMinimum}
          >
            {submitting ? 'Submitting...' : isBelowMinimum ? `Enter at least $${minUsdAmount || '—'}` : 'Submit Deposit'}
          </button>
        </div>
      </form>

      {/* Success Modal */}
      <SuccessModal
        isOpen={isSuccess}
        onClose={hideSuccess}
        title="Successful!"
        message="Your request has been received and is being processed."
        details={successDetails}
        onContinue={() => {
          hideSuccess();
          window.location.href = '/transaction-history';
        }}
      />
    </div>
  );
};

export default CryptoTransactionForm;